import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Heading,
  Text,
  Flex,
  Grid,
  Link,
  Input,
  Button,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  useDisclosure,
} from "@chakra-ui/react";
import { Phone, Send, Plus, Trash } from "lucide-react";
import useCustomTheme from "../../../hooks/useCustomTheme";
import { useAppContext } from "../../../contexts/AppContext";
import CloseModalArrow from "../../general/CloseModalArrow";

const SingleFundContacts = () => {
  const { fundName } = useParams();
  const { data } = useAppContext();
  const { theme } = useCustomTheme();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const formattedFundName = fundName.replace(/-/g, " ");
  const fund = data.find((fund) => fund.fundName === formattedFundName);

  // Local state for contacts and editing
  const [contacts, setContacts] = useState(fund ? fund.factSheet.contacts : []);
  const [editingContact, setEditingContact] = useState(null);
  const [newContact, setNewContact] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    position: "",
    phoneNumber: "",
    email: ""
  });

  if (!fund) return <Text>Select a fund to view details.</Text>;

  // Save or update a contact
  const handleSaveContact = () => {
    const updatedContacts = editingContact !== null
      ? contacts.map((contact, idx) => (idx === editingContact ? newContact : contact))
      : [...contacts, newContact];

    setContacts(updatedContacts);
    resetContactForm();
  };

  // Delete a contact
  const handleDeleteContact = (index) => {
    const updatedContacts = contacts.filter((_, idx) => idx !== index);
    setContacts(updatedContacts);
  };

  // Edit an existing contact
  const handleEditContact = (index) => {
    setEditingContact(index);
    setNewContact(contacts[index]);
    onOpen();
  };

  // Add a new contact
  const handleAddContact = () => {
    resetContactForm();
    onOpen();
  };

  // Reset contact form fields
  const resetContactForm = () => {
    setEditingContact(null);
    setNewContact({
      firstName: "",
      middleName: "",
      lastName: "",
      position: "",
      phoneNumber: "",
      email: ""
    });
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewContact((prev) => ({ ...prev, [name]: value }));
  };



  return (
    <Flex gap={8} direction="column">
      <Flex gap={2} justify={"space-between"}>
      <Heading size="md">Contacts</Heading>
      <Button size="sm" variant="outline" onClick={handleAddContact} leftIcon={<Plus size={12} />}>
        Add Contact
      </Button>
      </Flex>
      <Grid gap={8} templateColumns="repeat(1, 1fr)">
        {contacts.map((contact, index) => (
          <Flex key={index} gap={2} direction="column">
            <Flex gap={2} justify={"space-between"}>
              <Flex direction="column" gap={0}>
              <Text fontWeight="600" color="grey.700">
                {contact.firstName} {contact.middleName} {contact.lastName}
              </Text>
              <Text>{contact.position}</Text>
            </Flex>
            <Flex gap={2}>
              <Button
                variant={"outline"}
                size="xs"
                onClick={() => handleEditContact(index)}
                aria-label="Edit Contact"
              >
                Edit</Button>
              <IconButton
                icon={<Trash size={12} />}
                variant={"outline"}
                size="xs"
                onClick={() => handleDeleteContact(index)}
                aria-label="Delete Contact"
              />
            </Flex>
            </Flex>
            <Flex direction="column" gap={2} >
              <Link
                href={`tel:${contact.phoneNumber}`}
                fontSize="sm"
                color="brand.500"
                gap={1}
                display="flex"
                alignItems="center"
              >
                <Phone size={14} color={theme.colors.grey[500]} />
                {contact.phoneNumber}
              </Link>
              <Link
                href={`mailto:${contact.email}`}
                fontSize="sm"
                color="brand.500"
                gap={1}
                display="flex"
                alignItems="center"
              >
                <Send size={14} color={theme.colors.grey[500]} />
                {contact.email}
              </Link>
            </Flex>
            
          </Flex>
        ))}
      </Grid>
    

      <Modal isOpen={isOpen} onClose={resetContactForm}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader gap={4} alignItems={"center"}><CloseModalArrow onClick={resetContactForm} />{editingContact !== null ? "Edit Contact" : "Add New Contact"}</ModalHeader>
          <ModalBody>
            <Flex direction="column" gap={2}>
              <Input
                placeholder="First Name"
                name="firstName"
                value={newContact.firstName}
                onChange={handleChange}
              />
              <Input
                placeholder="Middle Name"
                name="middleName"
                value={newContact.middleName}
                onChange={handleChange}
              />
              <Input
                placeholder="Last Name"
                name="lastName"
                value={newContact.lastName}
                onChange={handleChange}
              />
              <Input
                placeholder="Position"
                name="position"
                value={newContact.position}
                onChange={handleChange}
              />
              <Input
                placeholder="Phone Number"
                name="phoneNumber"
                value={newContact.phoneNumber}
                onChange={handleChange}
              />
              <Input
                placeholder="Email"
                name="email"
                value={newContact.email}
                onChange={handleChange}
              />
            </Flex>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button onClick={resetContactForm} variant="outline">
              Cancel
            </Button>
            <Button colorScheme="teal" onClick={handleSaveContact}>
              {editingContact !== null ? "Save Changes" : "Add Contact"}
            </Button>
           
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default SingleFundContacts;
